/**
 * Internal, type-checked clipboard for board cells (monday.com-style
 * copy/paste). Deliberately NOT the OS clipboard: paste is restricted to the
 * same column type and cell values are structured (status option-id, `people`
 * id array, `date_range` object), so a typed in-app buffer is the only coherent
 * model. Holds a single entry; copying replaces it.
 *
 * Consumers should subscribe with a narrow selector — e.g.
 * `useCellClipboardStore((s) => s.clipboard?.type === columnType)` — so that a
 * copy only re-renders cells of that one type, not every mounted cell.
 */
import { create } from 'zustand';

import type { CustomColumnType } from './useTrackersBoard';

export interface CellClipboardEntry {
  type: CustomColumnType;
  value: unknown;
}

interface CellClipboardStore {
  clipboard: CellClipboardEntry | null;
  copy: (type: CustomColumnType, value: unknown) => void;
  clearClipboard: () => void;
}

export const useCellClipboardStore = create<CellClipboardStore>((set) => ({
  clipboard: null,
  copy: (type, value) => set({ clipboard: { type, value } }),
  clearClipboard: () => set({ clipboard: null }),
}));

/**
 * Column types whose cells expose copy/paste/clear. Excludes `button` (an
 * action trigger with no stored value), `formula` (derived from other columns,
 * so there is nothing of its own to carry) and the read-only brief-bound
 * columns (`project`, `brief_type`, `assets`).
 */
export const COPYABLE_COLUMN_TYPES: ReadonlySet<CustomColumnType> = new Set<CustomColumnType>([
  'text',
  'number',
  'date',
  'status',
  'people',
  'checkbox',
  'link',
  'date_range',
  'timeline',
  'team',
  'progress',
  'files',
  'approval',
]);

export const isCopyableType = (type: CustomColumnType | undefined): type is CustomColumnType =>
  type !== undefined && COPYABLE_COLUMN_TYPES.has(type);

/**
 * Whether a cell holds a clearable value — drives the **Clear** item's enabled
 * state so it's a no-op-free affordance. Handles each type's empty shape:
 * blank/whitespace strings, empty list values (`people`, `timeline`, `files`),
 * an `approval` without a state, and `date_range` objects
 * with neither endpoint set. `0` and `false`… note: an unchecked checkbox is
 * treated as empty (Clear only makes sense on a checked box).
 */
export function cellHasValue(type: CustomColumnType, value: unknown): boolean {
  if (value == null) return false;
  switch (type) {
    case 'text':
    case 'link':
    case 'date':
    case 'status':
    case 'team':
      return typeof value === 'string' && value.trim().length > 0;
    case 'number':
    case 'progress':
      return typeof value === 'number' && !Number.isNaN(value);
    case 'checkbox':
      return value === true;
    case 'people':
    case 'timeline':
    case 'files':
      return Array.isArray(value) && value.length > 0;
    case 'approval':
      return typeof value === 'object' && typeof (value as { state?: unknown }).state === 'string';
    case 'date_range': {
      if (typeof value !== 'object') return false;
      const range = value as { start?: unknown; end?: unknown };
      return Boolean(range.start) || Boolean(range.end);
    }
    default:
      return false;
  }
}
